import {JobStatus} from '@tinct/core-api-models';

/**
 * Which brand extraction the funnel is waiting for, one rule for the funnel's step availability and
 * for the Processing step that runs the extraction — they used to decide it each their own way, and
 * disagreed on a re-extraction.
 *
 * `organization.brandExtractionJob` is the LATEST extraction. When a new one is asked for, that is
 * the previous, finished job until the new one is created (by the Processing step itself, so after
 * the funnel has already decided whether to show it). Read naively it says "done", which would skip
 * Processing and never start the new extraction. So the job that was the latest when the extraction
 * was asked for is remembered, and never counts as the one being waited for.
 */

type ExtractionJob = { id?: string | null; status?: JobStatus | null };

export const FINISHED_STATUSES: readonly (JobStatus | null | undefined)[] = ['completed', 'failed', 'cancelled'];

/**
 * @param startNew whether an extraction has to be started, or one is already under way
 * @param previousJobId the job that was the latest when the extraction was asked for, ignored from
 *   then on; `null` when any latest job is the awaited one
 */
export type FollowedExtraction = { startNew: boolean; previousJobId: string | null };

/**
 * What the Processing step follows, decided on the organization's first load there:
 * - an extraction already under way (a reload mid-run, another tab) is followed, and no second one
 *   is started;
 * - otherwise a new one is started, and the finished latest job is the one to ignore.
 */
export const followOnLoad = (latestJob: ExtractionJob | null | undefined): FollowedExtraction => {
  const underWay = latestJob != null && !FINISHED_STATUSES.includes(latestJob.status);
  return {startNew: !underWay, previousJobId: underWay ? null : latestJob?.id ?? null};
};

/** The job of the awaited extraction once it exists; never the job that was the latest before it. */
export const awaitedJob = <J extends ExtractionJob>(
  latestJob: J | null | undefined,
  previousJobId: string | null,
): J | null => (latestJob && latestJob.id !== previousJobId ? latestJob : null);

/** Whether the awaited extraction has completed — `false` for as long as it does not even exist. */
export const isAwaitedExtractionCompleted = (
  latestJob: ExtractionJob | null | undefined,
  previousJobId: string | null,
): boolean => awaitedJob(latestJob, previousJobId)?.status === 'completed';
